import {
  Send, Sparkles, Paperclip, Loader2, FileText, ImageIcon, X, Brain,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { SaveChatButton } from './SaveChatButton';
import { PromptLibrary } from './PromptLibrary';

/**
 * Composer — chat input + toolbar block under the AI Hub message list.
 *
 * Extracted from /pages/AIHub.jsx (was inline at lines 911-1182 with all the
 * state lifted into the parent). Kept as a presentational, prop-driven
 * component so AIHub remains the single source of truth for chat state
 * (messages, attachment, intent, sending, etc.) — that state is shared
 * with the Hero composer and with sendMessage's auto-send lifecycle, so
 * lifting it down here would have required wrapping multiple branches in
 * a context provider for no behavioural gain.
 *
 * Two responsibilities:
 *  1. Render the textarea + attached file chip + vision warning + drag/drop
 *  2. Render the toolbar (Model picker, Attach, Prompt library, Save chat,
 *     Brain mode toggle, Image mode toggle, Memory, Send).
 */
export function Composer({
  // Attachment
  pendingAttachment,
  setPendingAttachment,
  // Vision warning
  modelLacksVision,
  activeModel,
  chosenModel,
  setChosenModel,
  suggestedVisionModel,
  // Drag & drop
  dragActive,
  setDragActive,
  uploadFile,
  // Input
  input,
  setInput,
  sending,
  handleKeyDown,
  // Model picker — parent already memoises this render fn so we just call it
  renderModelPicker,
  // Attach button
  fileInputRef,
  handleAttach,
  activeId,
  // Prompt library
  searchParams,
  setSearchParams,
  // Save chat
  currentWorkspace,
  // Mode toggles
  brainMode,
  setBrainMode,
  imageMode,
  setImageMode,
  setMemoryOpen,
  // Send
  sendMessage,
  // Free fallback hint
  showFreeFallbackHint,
  usage,
  freeFallbackModel,
}) {
  return (
    <div className="border-t border-zinc-200 dark:border-white/5 bg-white/60 dark:bg-black/40 backdrop-blur-xl px-3 sm:px-6 py-4">
      <div className="max-w-3xl mx-auto">
        {pendingAttachment && (
          <div
            className="mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-white/5 text-xs text-zinc-700 dark:text-zinc-300"
            data-testid="ai-hub-pending-attachment"
          >
            {pendingAttachment.kind === 'pdf_text' ? (
              <FileText className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
            ) : (
              <ImageIcon className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
            )}
            <span className="truncate flex-1">{pendingAttachment.original_name}</span>
            <button
              onClick={() => setPendingAttachment(null)}
              className="p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10"
              data-testid="ai-hub-remove-attachment"
              aria-label="Remove attachment"
            >
              <X className="w-3.5 h-3.5 text-zinc-500" />
            </button>
          </div>
        )}
        {modelLacksVision && (
          <div
            className="mb-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 text-xs text-amber-900 dark:text-amber-200"
            data-testid="ai-hub-vision-warning"
          >
            <ImageIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1 leading-relaxed">
              <span className="font-medium">{activeModel?.name || chosenModel}</span> can't read images.
              {suggestedVisionModel ? (
                <>
                  {' '}Switch to{' '}
                  <button
                    type="button"
                    onClick={() => setChosenModel(suggestedVisionModel.id)}
                    className="font-semibold underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-100"
                    data-testid="ai-hub-vision-switch-btn"
                  >
                    {suggestedVisionModel.name}
                  </button>
                  {' '}to use vision.
                </>
              ) : (
                <>
                  {' '}No vision-capable model is available on your tier — upgrade to use image attachments.
                </>
              )}
            </div>
          </div>
        )}
        <div
          className={cn(
            'relative rounded-2xl border bg-white/80 dark:bg-zinc-900/60 focus-within:border-emerald-500/60 dark:focus-within:border-emerald-500/40 transition-all shadow-sm',
            dragActive
              ? 'border-emerald-500 ring-4 ring-emerald-500/20 bg-emerald-50/40 dark:bg-emerald-950/20'
              : 'border-zinc-200 dark:border-white/10',
          )}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!dragActive) setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
          onDrop={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);
            const file = e.dataTransfer?.files?.[0];
            if (file) await uploadFile(file);
          }}
          data-testid="ai-hub-composer-dropzone"
        >
          {dragActive && (
            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none rounded-2xl bg-emerald-500/10 backdrop-blur-sm">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white text-xs font-semibold shadow-lg">
                <Paperclip className="w-4 h-4" /> Drop file to attach
              </div>
            </div>
          )}
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything — accounting, strategy, ad copy, contracts… (drop files here)"
            disabled={sending}
            rows={2}
            className="resize-none border-0 bg-transparent text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus-visible:ring-0 min-h-[72px] max-h-[200px] px-4 pt-3 pb-2"
            data-testid="ai-hub-composer-input"
          />
          {/* Toolbar — BELOW the textarea (modern chat UI style)
            * min-w-0 + the inner overflow-x-auto allow the toolbar to gracefully
            * shrink on narrow mobile viewports instead of forcing the whole
            * composer wider than the screen. */}
          <div className="flex items-center justify-between gap-1 px-2 pb-2 pt-1 border-t border-zinc-200/60 dark:border-white/5 min-w-0">
            <div className="flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-none">
              {/* Model picker — reuses the shared renderModelPicker() so the
                * dropdown is identical to the one in the Hero. */}
              {renderModelPicker('sm')}

              <div className="w-px h-5 bg-zinc-200 dark:bg-white/10 mx-1" />

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,application/pdf"
                onChange={handleAttach}
                data-testid="ai-hub-file-input"
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 gap-1.5 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/5"
                onClick={() => fileInputRef.current?.click()}
                disabled={!activeId}
                title="Attach PDF or image (or drop into the composer)"
                data-testid="ai-hub-attach-btn"
              >
                <Paperclip className="w-3.5 h-3.5" />
                <span className="text-xs hidden sm:inline">Attach</span>
              </Button>
              <PromptLibrary
                onSelectPrompt={(p) => setInput(p)}
                initialHubFilter={searchParams.get('promptHub')}
                onConsumed={() => {
                  if (searchParams.get('promptHub')) {
                    searchParams.delete('promptHub');
                    setSearchParams(searchParams, { replace: true });
                  }
                }}
                triggerClassName="h-8 px-2 border-0 bg-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/5 shadow-none"
              />
              {activeId && (
                <SaveChatButton
                  threadId={activeId}
                  workspaceId={currentWorkspace?.workspace_id}
                  triggerClassName="h-8 px-2 border-0 bg-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/5 shadow-none"
                />
              )}
              <div className="w-px h-5 bg-zinc-200 dark:bg-white/10 mx-1" />
              {/* Brain mode toggle — ON = workspace-aware tool-calling, OFF = generic chat */}
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setBrainMode((v) => !v)}
                      className={cn(
                        'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium transition-colors',
                        brainMode
                          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/25'
                          : 'text-zinc-500 dark:text-zinc-400 hover:bg-black/5 dark:hover:bg-white/5',
                      )}
                      data-testid="ai-hub-brain-toggle"
                      aria-label={brainMode ? 'Workspace AI is on' : 'Workspace AI is off'}
                    >
                      <Brain className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{brainMode ? 'Brain on' : 'Generic'}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center" className="max-w-[280px] leading-snug">
                    <div className="font-semibold mb-1">
                      {brainMode ? '🧠 Brain mode is ON' : '🧠 Brain mode is OFF'}
                    </div>
                    <p className="text-[11px] opacity-90">
                      {brainMode
                        ? 'The AI can read your live workspace data — invoices, bills, contacts, cash flow — across every workspace you have access to, and answers questions with real numbers. Click to switch to plain chat.'
                        : 'You\'re in plain chat. The AI has no access to your books. Click to turn on Brain mode and let it pull live numbers from your workspaces.'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Image-mode toggle — when ON, every message is routed
                  to Nano Banana so users can refine an existing image
                  with short prompts ("darker", "add a coffee cup")
                  without re-stating "generate me an image of …". The
                  composer indicator and tooltip make the capability
                  discoverable without taking real estate when off. */}
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setImageMode((v) => !v)}
                      className={cn(
                        'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium transition-colors',
                        imageMode
                          ? 'bg-violet-500/15 text-violet-700 dark:text-violet-400 hover:bg-violet-500/25'
                          : 'text-zinc-500 dark:text-zinc-400 hover:bg-black/5 dark:hover:bg-white/5',
                      )}
                      data-testid="ai-hub-image-toggle"
                      aria-label={imageMode ? 'Image mode is on' : 'Image mode is off'}
                    >
                      <ImageIcon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{imageMode ? 'Image on' : 'Image'}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center" className="max-w-[280px] leading-snug">
                    <div className="font-semibold mb-1">
                      {imageMode ? '🎨 Image mode is ON' : '🎨 Image mode is OFF'}
                    </div>
                    <p className="text-[11px] opacity-90">
                      {imageMode
                        ? 'Every message generates an image via Gemini Nano Banana. Great for refining ("darker", "with a coffee mug"). Click to turn off.'
                        : 'Click to force image generation for the next message — useful when refining an existing image. You can also just ask naturally ("create a logo of …") without toggling.'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Workspace Memory — opens the pinned-facts sheet so the
                  user can pin a fact the AI should remember in every
                  future conversation. Also accessible via `/remember`. */}
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setMemoryOpen(true)}
                      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium transition-colors text-zinc-500 dark:text-zinc-400 hover:bg-black/5 dark:hover:bg-white/5"
                      data-testid="ai-hub-memory-btn"
                      aria-label="Open Workspace Memory"
                    >
                      <Brain className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Memory</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center" className="max-w-[280px] leading-snug">
                    <div className="font-semibold mb-1">🧠 Workspace Memory</div>
                    <p className="text-[11px] opacity-90">
                      Pin long-lived facts (fiscal year, vendors, signing names) so the AI doesn't ask the same questions every conversation. Or type <code className="text-[10px] bg-muted px-1 rounded">/remember &lt;fact&gt;</code> in chat.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Button
              size="sm"
              className="h-8 px-3 gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-40"
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              data-testid="ai-hub-send-btn"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span className="text-xs">Send</span>
            </Button>
          </div>
        </div>
        {showFreeFallbackHint && (
          <div
            className="mt-2 mx-auto max-w-fit flex items-center gap-2 rounded-full border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1 text-[11px] text-emerald-800 dark:text-emerald-300 shadow-sm"
            data-testid="ai-hub-fallback-hint"
          >
            <Sparkles className="w-3 h-3 text-emerald-500 flex-shrink-0" />
            <span>
              {usage.ai_pct_used}% of premium cap used.
              Switch to <button
                type="button"
                onClick={() => setChosenModel(freeFallbackModel.id)}
                className="font-semibold underline hover:text-emerald-900 dark:hover:text-emerald-200"
                data-testid="ai-hub-fallback-switch"
              >{freeFallbackModel.name}</button> to save credits for general questions
              <span className="opacity-70"> (note: free models can't read your live workspace data)</span>.
            </span>
          </div>
        )}
        <div className="mt-2 flex items-center justify-center gap-2 text-[11px] text-zinc-500 dark:text-zinc-600">
          <span>
            {activeModel?.is_free ? 'Free tier — answers may vary. Upgrade for premium models.' : 'AI can make mistakes. Verify important info.'}
          </span>
        </div>
      </div>
    </div>
  );
}
